/**
 * Error logging utility for Vercel
 * 
 * In production on Vercel, console.error logs are automatically captured
 * in Vercel's Runtime Logs. This utility provides a consistent interface
 * for logging errors with structured context.
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  formData?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Log an error to Vercel's logging system
 * Errors logged here will appear in Vercel's Runtime Logs dashboard
 */
export function logError(
  error: Error | unknown,
  context?: ErrorContext
): void {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    message: error instanceof Error ? error.message : String(error),
    name: error instanceof Error ? error.name : 'UnknownError',
    stack: error instanceof Error ? error.stack : undefined,
    ...context,
  };

  // Log locally for debugging
  console.error('[Error]', JSON.stringify(errorInfo, null, 2));

  // Send to Vercel API route so it gets captured in Runtime Logs
  fetch('/api/logError', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(errorInfo),
  }).catch((e) => console.error('Failed to log error to server', e));
}


/**
 * Log a form submission error
 */
export function logFormError(
  error: Error | unknown,
  formName: string,
  formData?: Record<string, unknown>
): void {
  // Sanitize form data to avoid logging sensitive information
  const sanitizedFormData = formData ? {
    ...formData,
    email: formData.email ? '***@***.***' : undefined,
  } : undefined;

  logError(error, {
    type: 'form_submission',
    formName,
    formData: sanitizedFormData,
  });
}
